import { useMemo } from 'react';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import { useCachedSelectedProject } from './useCachedSelectedProject'; // same directory
import {
  GeographicFocusRequest,
  GeographicFocusResponse,
  InfoCompletenessScoring,
} from 'store/api/apiTypes';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

/**
 * useCachedGeographicFocus
 * Access an already-fetched geographic focus rule result from RTK Query cache without re-fetching
 */
export const useCachedGeographicFocus = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<GeographicFocusRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectGeoFocus = useMemo(
    () =>
      queryArgs
        ? rulesApiSlice.endpoints.geographicFocus.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );
  const selectResult = useAppSelector(selectGeoFocus as any) as {
    data: GeographicFocusResponse | undefined;
    status: string;
    isLoading?: boolean;
    isError?: boolean;
    error?: any;
  };
  const { data, status, isLoading: loading, isError, error } = selectResult;
  const geographicFocus = data as GeographicFocusResponse | undefined;

  let passCount = 0;
  const rulesMap = geographicFocus?.company_analysis?.scoring_breakdown as
    | Record<string, InfoCompletenessScoring>
    | undefined;
  if (rulesMap) {
    Object.values(rulesMap).forEach(rule => {
      if (rule.score >= 8) {
        passCount += 1;
      }
    });
  }

  const isLoaded =
    !!geographicFocus?.company_analysis && !loading && !isGenerating;
  const isLoading = loading || isGenerating;
  return {
    geographicFocus,
    companyAnalysis: geographicFocus?.company_analysis,
    productAnalyses: geographicFocus?.products || {},
    companyConfidence:
      geographicFocus?.company_analysis?.geographic_focus_identified
        .confidence_level,
    companyEvidance:
      geographicFocus?.company_analysis?.geographic_focus_identified
        .evidence_strength,
    scoring: geographicFocus?.company_analysis?.scoring_breakdown,
    score: geographicFocus?.company_analysis?.final_score,
    urlsAnalyzed: geographicFocus?.company_analysis?.page_urls_analyzed,
    extraction: geographicFocus?.company_analysis?.geographic_focus_extraction,
    identified: {
      primary:
        geographicFocus?.company_analysis?.geographic_focus_identified
          .primary_geographic_focus,
      confidence_level:
        geographicFocus?.company_analysis?.geographic_focus_identified
          .confidence_level,
      evidence_strength:
        geographicFocus?.company_analysis?.geographic_focus_identified
          .evidence_strength,
    },
    status,
    isLoading,
    isLoaded,
    isError,
    error,
    queryArgs,
    passed: passCount,
    rulesCount: Object.keys(
      geographicFocus?.company_analysis?.scoring_breakdown || {}
    ).length,
  };
};
